import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription, Subject, BehaviorSubject } from 'rxjs';

import { environment } from 'src/environments/environment';
import { NavigationItem } from '../models/navigation-item.model';
import { DemographicContentService } from './demographic-content.service';
import { IframeService } from './iframe.service';
import { LoggingMessageService } from './logging-message.service';
import { UserNavigationService } from './user-navigation.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { SubscriptionChangeService } from 'src/app/services/subscription-change.service';


@Injectable({
    providedIn: 'root'
})
export class WindowEventsService implements OnInit, OnDestroy {
    private $subscriptions: Subscription = new Subscription();

    private indicatorResizingSource = new Subject<number>();
    public indicatorResizing$ = this.indicatorResizingSource.asObservable();

    private indicatorRebuiltSource = new Subject<number>();
    public indicatorRebuilt$ = this.indicatorRebuiltSource.asObservable();

    private indicatorVersionSource = new Subject<string>();
    public indicatorVersion$ = this.indicatorVersionSource.asObservable();

    private sysManVersionSource = new Subject<string>();
    public sysManVersion$ = this.sysManVersionSource.asObservable();

    private perfManVersionSource = new Subject<string>();
    public perfManVersion$ = this.perfManVersionSource.asObservable();

    private validationEngineVersionSource = new Subject<string>();
    public validationEngineVersion$ = this.validationEngineVersionSource.asObservable();

    private publicReportVersionSource = new Subject<string>();
    public publicReportVersion$ = this.publicReportVersionSource.asObservable();


    private indicatorRerouteSource = new Subject<string>();
    public indicatorReroute$ = this.indicatorRerouteSource.asObservable();

    private portalActionSource = new Subject<any>();
    public portalAction$ = this.portalActionSource.asObservable();

    private portalSessionSource = new Subject<any>();
    public portalSession$ = this.portalSessionSource.asObservable();

    private subDomainActionSource = new Subject<any>();
    public subDomainAction$ = this.subDomainActionSource.asObservable();

    private portalLoginSource = new Subject<boolean>();
    public portalLogin$ = this.portalLoginSource.asObservable();

    private indicatorLogoutSource = new Subject<boolean>();
    public indicatorLogout$ = this.indicatorLogoutSource.asObservable();

    private portalLogoutSource = new Subject<boolean>();
    public portalLogout$ = this.portalLogoutSource.asObservable();

    private portalRerouteSource = new Subject<string>();
    public portalRerouteSource$ = this.portalRerouteSource.asObservable();

    private publicReportRerouteSource = new Subject<string>();
    public publicReportReroute$ = this.publicReportRerouteSource.asObservable();

    private publicReportPidReReoute = new Subject<NavigationItem>();
    public publicReportPidReReoute$ = this.publicReportPidReReoute.asObservable();

    private pirReReouteSource = new Subject<string>();
    public pirReReoute$ = this.pirReReouteSource.asObservable();

    public publicReportPrevUrl  = new BehaviorSubject<string>('');
    private publicReportPrevUrl$ = this.publicReportPrevUrl.asObservable();

    private _indicatorFactoryVersion: string = 'v0.0';
    get IndicatorFactoryVersion(): string {
      return this._indicatorFactoryVersion;
    }
    set IndicatorFactoryVersion(value: string) {
      this._indicatorFactoryVersion = value;
    }

    constructor(private iframeService: IframeService,
        private logger: LoggingMessageService,
        private sessionService: SessionStorageService,
        private router: Router,
        private demographicContentService: DemographicContentService,
        private navigationService: UserNavigationService,
        private subscriptionService: SubscriptionChangeService) {
        this.subscribeToWindowsEvents();
    }

    ngOnInit(): void { }

    private subscribeToWindowsEvents() {
        const target = trimWebServer(environment.WebServer);
        if (window.addEventListener) { // Listen message from remote (iFrame)
            window.addEventListener('message', this.recieveMessageFromiFrame.bind(this, target), false);
        }
    }

    private publishIndicatorResizing(size: any) {
        this.indicatorResizingSource.next(size);
    }

    private publishIndicatorVersion(version: any) {
        this.indicatorVersionSource.next(version);
    }

    private publishSystemManagementVersion(version: any) {
        this.sysManVersionSource.next(version);
    }

    private publishPerfManagementVersion(version: any) {
        this.perfManVersionSource.next(version);
    }

    private publishValidationEngineVersion(version: any) {
        this.validationEngineVersionSource.next(version);
    }

    private publishPublicReportVersion(version: any) {
        this.publicReportVersionSource.next(version);
    }

    private publishIndicatorRebuilt(indicatorid: any) {
        this.indicatorRebuiltSource.next(indicatorid);
    }

    private publishIndicatorReroute(link: string) {
        this.iframeService.setLinkId(link);
        this.indicatorRerouteSource.next(link);
    }

    private publishPirReroute(link: string) {
        this.pirReReouteSource.next(link);
    }

    private publishPublicReportReroute(link: string) {
        const prevUrl = this.router.url;
        this.publicReportPrevUrl.next(prevUrl)
        this.publicReportRerouteSource.next(link);
    }

    private publishPortalLogin() {
        this.portalLoginSource.next(true);
    }

    private publishPortalAction() {
        this.portalActionSource.next(true);
    }

    private publishPortalSession() {
        this.portalSessionSource.next(true);
    }

    private publishPortalLogout() {
        this.portalLogoutSource.next(true);
    }

    private publishSubDomainAction(action: string) {
        this.subDomainActionSource.next(action);
    }

    private publishIndicatorLogout() {
        this.indicatorLogoutSource.next(true);
    }

    private publishPortalReroute(link: string) {
        this.portalRerouteSource.next(link);
    }

    public setPublicReportPidReReoute(navItem) {
        this.publicReportPidReReoute.next(navItem);
    }

    private recieveMessageFromiFrame(target: string, e: any) {
        this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Received message. Target: ${target}`, '');
        const origin = (e.origin) ? e.origin.toString() : '';
        this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Received message. Origin: ${origin}`, '');
        const isValidOrigin = origin.startsWith(target);
        if (isValidOrigin && (e.data) && !(e.data.type)) {
            // this.logger.info('window.event.service.ts', 'recieveMessageFromiFrame', `Received message. Finding Action Data: ${e.data}`, '');
            const dataString = e.data.toString();
            // this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Received message. Data: ${dataString}`, '');
            this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Received message. Data: ${dataString}`, '');
            const action = dataString.split(':')[0];
            this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  `Received message. Found Action: ${action}`, '');
            switch (action) {
                case 'sizing':
                    const size = dataString.split(':')[1];
                    this.publishIndicatorResizing(size);
                    break;
                case 'scrollTop':
                    window.scrollTo(0, 0);
                    break;
                case 'indicatorversion':
                    const ifversion = dataString.split(':')[1];
                    this.publishIndicatorVersion(ifversion);
                    break;
                    case 'sysmanversion':
                    const smversion = dataString.split(':')[1];
                    this.publishSystemManagementVersion(smversion);
                    break;
                case 'perfmanversion':
                    const pmversion = dataString.split(':')[1];
                    this.publishPerfManagementVersion(pmversion);
                    break;
                case 'validationengineversion':
                        const veversion = dataString.split(':')[1];
                        this.publishValidationEngineVersion(veversion);
                        break;
                case 'publicreportversion':
                const prversion = dataString.split(':')[1];
                    console.log(`Public Report Version: ${prversion}`);
                    this.publishPublicReportVersion(prversion);
                    break;
                case 'indicatorbuild':
                    const indicatorid = dataString.split(':')[1];
                    this.publishIndicatorRebuilt(indicatorid);
                    break;
                case 'rerouteIndicator':
                    const link = dataString.split(':')[1];
                    this.publishIndicatorReroute(link);
                    break;
                case 'reroutePortal':
                    const portalLink = dataString.split(':')[1];
                    this.publishPortalReroute(portalLink);
                    break;
                case 'portallogin':
                    this.publishPortalLogin();
                    break;
                case 'portalaction':
                    this.publishPortalAction();
                    break;
                case 'portalsession':
                        this.publishPortalSession();
                        break;
                case 'portallogout':
                    this.logger.warn('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Logout message received. Action: ${action}`, '');
                    this.publishPortalLogout();
                    break;
                case 'portalsessionlogout':
                    //("Logout message received from Portal login page: " + action);
                    this.logger.warn('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Logout message received from Portal login page. Action: ${action}`, '');
                    this.publishPortalLogout();
                    break;
                case 'performancereportsaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'indicatorfactoryaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'validationengineaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'systemmanagementaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'publicreportsaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'ndnqifoundationaction':
                    this.publishSubDomainAction(action);
                    break;
                case 'mstraction':
                    // console.log("recieveMessageFromiFrame: mstraction: " + action);
                    // console.log("recieveMessageFromiFrame: taking NO action: ");
                    // this.publishSubDomainAction(action);
                    break;
                case 'logout':
                    this.logger.debug('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Logout message received. Action: ${action}`, '');
                    this.publishIndicatorLogout();
                    break;
                case 'publicreport':
                    const pr = dataString.split(':')[1];
                    this.publishPublicReportReroute(pr);
                    break;
                case 'rerouteunit':
                    const data = e.data.split(':')[1];
                    const hospid = data.split(',')[0];
                    const unitid = data.split(',')[1];
                    const unitLinkId = `s0_f${hospid}_u${unitid}`;

                    this.iframeService.setNdnqiPublicReportsFrameVisibility(false);
                    const cachedUserUnitNav = this.navigationService.CachedNavigationMenu;
                    const navUnitItem = cachedUserUnitNav.getNavigationItemUsingPermID(unitLinkId);
                    this.setPublicReportPidReReoute(navUnitItem);
                    break;
                case 'rerouteindicator':
                    const linkId = e.data.split(':')[1];
                    const cachedUserNav = this.navigationService.CachedNavigationMenu;
                    const navItem = cachedUserNav.getNavigationItemUsingPermID(linkId);
                    this.setPublicReportPidReReoute(navItem);
                    break;
                case 'showdemographics':
                  const item = e.data.split(':')[1];
                  this.demographicContentService.setSource(item);
                  break;
                case 'hidedemographics':
                  const item1 = e.data.split(':')[1];
                  this.demographicContentService.setSource(null);
                  break;
                case 'reroutePir':
                    const l = e.data.split(':')[1];
                    // console.log('PIR Receive', l);
                    this.publishPirReroute(l);
                    break;
                default:
                    this.logger.info('window.event.service.ts', 'recieveMessageFromiFrame', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Received message. Unknown Action: ${action}`, '');
                    break;
            }
        }


        //  rows= $('.mstrmojo-XtabZone').find('table').find('tr');
        //  for (i = 0; i < rows.length; i++) {
        //   var currentRow = rows[i];
        //   var firstTd = currentRow.getElementsByTagName("td")[0];
        //   firstTd.addEventListener('mouseover', function (event) {
        //     var id = event.target.innerHTML;
        //     console.log('Demographic Enter Send', id);
        //     window.parent.postMessage('showdemographics:' + id, '*');
        //    });
        //    firstTd.addEventListener('mouseout', function (event) {
        //     var id = event.target.innerHTML;
        //     console.log('Demographic Leave Send', id);
        //     window.parent.postMessage('hidedemographics:' + id, '*');
        //    });
        //   }
    }

    unsubscribe() {
        if (this.$subscriptions) {
            this.$subscriptions.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}

export function trimWebServer(webserver: string): string {
    let target = '';
    if ((webserver) && (webserver.endsWith('/', webserver.length))) {
        target = webserver.substring(0, webserver.length - 1);
    } else {
        target = webserver;
    }
    const targetparts = target.split(':');
    const stripPort = `${targetparts[0]}:${targetparts[1]}`;
    return stripPort;
}
